import dayjs from 'dayjs'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import { EndType, IntervalType, ScheduleRRuleOptions, ShortMonths, ShortWeekDays } from '../../const'
import { useVueI18n } from '../../locales'
import { getCurrentDate, getFormatDate } from '../../utils'
const t = useVueI18n()

const ALLOWED_DAYS = [...Array.from(Array(31), (_, i) => i + 1), -1]

// wrapped in a function (rather than a plain top-level const) so t(...) is re-evaluated
// against the current locale every time a schema is built, instead of being baked in once
// at module-import time
export const buildRRuleSchema = () =>
  yup.object({
  intervalType: yup
    .mixed()
    .oneOf(Object.values(IntervalType), t('validations.enum.intervalType'))
    .required(t('validations.invalid.intervalType')),
  interval: yup
    .number()
    .typeError(t('validations.invalid.numberInterval'))
    .max(99)
    .positive(t('validations.invalid.positiveInterval'))
    .integer(t('validations.invalid.integerInterval'))
    .required(t('validations.invalid.interval')),

  dayOfMonth: yup.mixed().when(['intervalType', 'numOfWeek', 'monthOfYear'], (intervalType, numOfWeek, monthOfYear) => {
    if ((intervalType === IntervalType.MONTHLY && numOfWeek === 0) || intervalType === IntervalType.YEARLY) {
      return yup
        .mixed()
        .oneOf(ALLOWED_DAYS, t('validations.enum.dayOfMonth'))
        .required(t('validations.invalid.dayOfMonth'))
        .test('yearly-day-in-month', t('validations.invalid.dayOfMonth'), function (value) {
          if (intervalType !== IntervalType.YEARLY) return true
          const monthKey = monthOfYear?.toLowerCase()
          const day = Number(value)
          if (!monthKey) return true
          // Feb: 30 and 31 invalid
          if (monthKey === 'feb' && (day === 30 || day === 31)) return false
          // Apr, Jun, Sep, Nov: 31 invalid
          if (['apr', 'jun', 'sep', 'nov'].includes(monthKey) && day === 31) return false
          return true
        })
    }
  }),
  dayOfWeek: yup.mixed().when(['intervalType', 'numOfWeek'], (intervalType, numOfWeek) => {
    if (intervalType === IntervalType.WEEKLY || (intervalType === IntervalType.MONTHLY && numOfWeek !== 0)) {
      return yup
        .mixed()
        .oneOf(Object.values(ShortWeekDays), t('validations.enum.dayOfWeek'))
        .required(t('validations.invalid.dayOfWeek'))
    }
  }),
  numOfWeek: yup.mixed().when('intervalType', intervalType => {
    if (intervalType === IntervalType.MONTHLY)
      return yup
        .mixed()
        .oneOf([-1, 0, 1, 2, 3, 4], t('validations.enum.numOfWeek'))
        .required(t('validations.invalid.numOfWeek'))
  }),
  monthOfYear: yup.mixed().when('intervalType', intervalType => {
    if (intervalType === IntervalType.YEARLY)
      return yup
        .mixed()
        .oneOf(Object.values(ShortMonths), t('validations.enum.monthOfYear'))
        .required(t('validations.invalid.monthOfYear'))
  }),
  startDate: yup.mixed().when('useStartAsPrimaryUserSigned', useStartAsPrimaryUserSigned => {
    if (!useStartAsPrimaryUserSigned)
      return yup
        .date()
        .typeError(t('validations.invalid.dateStartDate'))
        .min(getCurrentDate(), `${t('validations.min.startDate')} ${getCurrentDate()}`)
        .required(t('validations.invalid.startDate'))
  }),
  startTime: yup.mixed().when('intervalType', intervalType => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType))
      return yup.string().typeError(t('validations.invalid.stringStartTime'))
  }),
  endType: yup
    .mixed()
    .oneOf(Object.values(EndType), t('validations.enum.endType'))
    .required(t('validations.invalid.endType')),
  endDate: yup.mixed().when(['endType', 'startDate'], (endType, startDate) => {
    if (endType === EndType.BY) {
      return yup
        .date()
        .typeError(t('validations.invalid.dateEndDate'))
        .test('is-greater-than', t('validations.min.endDate'), function (val) {
          const { path, createError } = this
          const currentDate = getCurrentDate()
          const start = startDate && startDate !== 'Invalid Date' ? getFormatDate(startDate) : null

          const isPastCurrantDate = dayjs(val).isBefore(currentDate, 'day')
          const isPastStartDate = start ? dayjs(val).isBefore(start, 'day') : false

          if (isPastCurrantDate) {
            return createError({
              path,
              message: `${t('validations.min.endDate')} ${getCurrentDate()}`,
            })
          }

          if (isPastStartDate) {
            return createError({
              path,
              message: `${t('validations.min.endDate')} ${getFormatDate(startDate)}`,
            })
          }

          return true
        })
        .required(t('validations.invalid.endDate'))
    }
  }),
  endTime: yup.mixed().when(['endDate', 'intervalType'], (endDate, intervalType) => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType) && endDate)
      return yup.string().required(t('validations.invalid.endTime'))
  }),
  count: yup.mixed().when('endType', endType => {
    if (endType === EndType.AFTER)
      return yup
        .number()
        .typeError(t('validations.invalid.numberCount'))
        .max(99)
        .nullable()
        .positive(t('validations.invalid.positiveCount'))
        .integer()
        .required(t('validations.invalid.count'))
  }),
  daysBefore: yup.mixed().when(['intervalType', 'useStartAsPrimaryUserSigned'], (intervalType, useStartAsPrimaryUserSigned) => {
    if (!useStartAsPrimaryUserSigned && [IntervalType.WEEKLY, IntervalType.MONTHLY, IntervalType.YEARLY].includes(intervalType)) {
      const max = intervalType === IntervalType.WEEKLY ? 7 : 28
      return yup
        .number()
        .typeError(t('validations.invalid.numberDaysBefore'))
        .min(0)
        .max(max, `${t('validations.max.daysBefore')} ${max}`)
        .nullable()
        .integer()
        .required(t('validations.invalid.daysBefore'))
    }
  }),
  useStartAsPrimaryUserSigned: yup
    .boolean()
    .typeError(t('validations.invalid.booleanUseStartAsPrimaryUserSigned'))
    .required(t('validations.required.useStartAsPrimaryUserSigned')),
  })

export const buildRRuleSchemaV2 = () =>
  yup.object({
  intervalType: yup
    .mixed()
    .oneOf(Object.values(IntervalType), t('validations.enum.intervalType'))
    .required(t('validations.invalid.intervalType')),

  endType: yup
    .mixed()
    .oneOf(Object.values(EndType), t('validations.enum.endType'))
    .required(t('validations.invalid.endType')),
  endDate: yup.mixed().when(['endType', 'startDate'], (endType, startDate) => {
    if (endType === EndType.BY) {
      return yup
        .date()
        .typeError(t('validations.invalid.dateEndDate'))
        .test('is-greater-than', t('validations.min.endDate'), function (val) {
          const { path, createError } = this
          const currentDate = getCurrentDate()
          const start = startDate && startDate !== 'Invalid Date' ? getFormatDate(startDate) : null

          const isPastCurrantDate = dayjs(val).isBefore(currentDate, 'day')
          const isPastStartDate = start ? dayjs(val).isBefore(start, 'day') : false

          if (isPastCurrantDate) {
            return createError({
              path,
              message: `${t('validations.min.endDate')} ${getCurrentDate()}`,
            })
          }

          if (isPastStartDate) {
            return createError({
              path,
              message: `${t('validations.min.endDate')} ${getFormatDate(startDate)}`,
            })
          }

          return true
        })
        .required(t('validations.invalid.endDate'))
    }
  }),
  endTime: yup.mixed().when(['endDate', 'intervalType'], (endDate, intervalType) => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType) && endDate)
      return yup.string().required(t('validations.invalid.endTime'))
  }),
  count: yup.mixed().when('endType', endType => {
    if (endType === EndType.AFTER)
      return yup
        .number()
        .typeError(t('validations.invalid.numberCount'))
        .max(99)
        .nullable()
        .positive(t('validations.invalid.positiveCount'))
        .integer()
        .required(t('validations.invalid.count'))
  }),
  daysBefore: yup.mixed().when(['intervalType', 'useStartAsPrimaryUserSigned'], (intervalType, useStartAsPrimaryUserSigned) => {
    if (!useStartAsPrimaryUserSigned && [IntervalType.WEEKLY, IntervalType.MONTHLY, IntervalType.YEARLY].includes(intervalType)) {
      const max = intervalType === IntervalType.WEEKLY ? 7 : 28
      return yup
        .number()
        .typeError(t('validations.invalid.numberDaysBefore'))
        .min(0)
        .max(max, `${t('validations.max.daysBefore')} ${max}`)
        .nullable()
        .integer()
        .required(t('validations.invalid.daysBefore'))
    }
  }),
  })

export const getSchedulingSettingsForm = (rrule: ScheduleRRuleOptions) => {
  return useForm({
    validationSchema: buildRRuleSchema(),
    initialValues: {
      intervalType: rrule?.intervalType || '',
      interval: rrule?.interval || 0,
      dayOfMonth: rrule?.dayOfMonth || '',
      dayOfWeek: rrule?.dayOfWeek || '',
      numOfWeek: rrule?.numOfWeek || 0,
      monthOfYear: rrule?.monthOfYear,
      startTime: rrule?.startTime || '',
      endType: rrule?.endType || '',
      endTime: rrule?.endTime || '',
      count: rrule?.count || '',
      daysBefore: rrule?.daysBefore || 0,
      startDate: rrule?.startDate || '',
      endDate: rrule?.endDate || '',
      useStartAsPrimaryUserSigned: rrule?.useStartAsPrimaryUserSigned ?? true,
    },
  })
}

export const getSchedulingSettingsFormV2 = (rrule: ScheduleRRuleOptions) => {
  return useForm({
    validationSchema: buildRRuleSchemaV2(),
    initialValues: {
      intervalType: (rrule?.intervalType || IntervalType.WEEKLY) as IntervalType,
      interval: rrule?.interval || 0,
      dayOfMonth: rrule?.dayOfMonth || '',
      dayOfWeek: rrule?.dayOfWeek || '',
      numOfWeek: rrule?.numOfWeek || 0,
      monthOfYear: rrule?.monthOfYear,
      startTime: rrule?.startTime || '',
      endType: rrule?.endType || EndType.NEVER,
      endTime: rrule?.endTime || '',
      count: rrule?.count || '',
      daysBefore: rrule?.daysBefore || 0,
      startDate: rrule?.startDate || '',
      endDate: rrule?.endDate || '',
      useStartAsPrimaryUserSigned: rrule?.useStartAsPrimaryUserSigned ?? true,
    },
  })
}

export const initialiseScheduleErrorsIfNotExists = (schedule: ScheduleRRuleOptions, generateInvoiceOnSigning: boolean) => {
  if (generateInvoiceOnSigning) {
    const { validate } = getSchedulingSettingsFormV2(schedule)
    return validate()
  } else {
    const { validate } = getSchedulingSettingsForm(schedule)
    return validate()
  }
}
